import { ArrowDownIcon, ArrowLeftIcon, ArrowRightIcon, ArrowUpIcon } from '@radix-ui/react-icons';

/**
 * The 100 buckets divided into four 5×5 quadrants in clockwise order:
 * Q0 = Top-Left, Q1 = Top-Right, Q2 = Bottom-Right, Q3 = Bottom-Left.
 *
 * Each entry is the flat list of bucket indices that belong to that quadrant.
 * Indices follow the formula: `row * 10 + col` (0-indexed, 10 columns total).
 */
export const QUADRANTS: readonly number[][] = [
  // Q0 - Top-Left (rows 0–4, cols 0–4)
  Array.from({ length: 5 }, (_, row) =>
    Array.from({ length: 5 }, (_, col) => row * 10 + col),
  ).flat(),
  // Q1 — Top-Right (rows 0–4, cols 5–9)
  Array.from({ length: 5 }, (_, row) =>
    Array.from({ length: 5 }, (_, col) => row * 10 + col + 5),
  ).flat(),
  // Q2 — Bottom-Right (rows 5–9, cols 5–9)
  Array.from({ length: 5 }, (_, row) =>
    Array.from({ length: 5 }, (_, col) => (row + 5) * 10 + col + 5),
  ).flat(),
  // Q3 — Bottom-Left (rows 5–9, cols 0–4)
  Array.from({ length: 5 }, (_, row) =>
    Array.from({ length: 5 }, (_, col) => (row + 5) * 10 + col),
  ).flat(),
];

/**
 * Arrow icon to display on the "Next quadrant" button for each active quadrant.
 *
 * The direction indicates the clockwise step from the current quadrant to the next:
 * 1 - Q0 Top-Left      ->  right   ->    Q1 Top-Right
 * 2 - Q1 Top-Right     ->  down    ->    Q2 Bottom-Right
 * 3 - Q2 Bottom-Right  ->  left    ->    Q3 Bottom-Left
 * 4 - Q3 Bottom-Left   ->  up      ->    Q0 Top-Left
 */
export const QUADRANT_NEXT_ICONS = [
  ArrowRightIcon, // 1
  ArrowDownIcon, // 2
  ArrowLeftIcon, // 3
  ArrowUpIcon, // 4
] as const;

/**
 * Rendering order of the four quadrants in the 2×2 visual grid.
 *
 * The grid is laid out as:
 * ```
 * [ Q0 TL ]  [ Q1 TR ]
 * [ Q3 BL ]  [ Q2 BR ]
 * ```
 * Note that Q3 and Q2 are swapped relative to their clockwise index order
 * so that the bottom row reads left-to-right visually.
 */
export const GRID_QUADRANT_ORDER = [0, 1, 3, 2] as const;
